import { useRef, useState } from 'react'
import {
  Stack, Text, Textarea, Button, Group, Avatar,
  Badge, Paper, Switch, Divider, ActionIcon, FileButton, Progress,
  TypographyStylesProvider, Tabs, Tooltip,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'
import {
  IconSend, IconLock, IconPaperclip, IconFile, IconX, IconCheck,
  IconDownload, IconBold, IconItalic, IconCode, IconList, IconLink,
  IconHeading, IconEye, IconPencil, IconTrash,
} from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { api } from '@/api/client'
import { formatApiError } from '@/api/errors'
import { runWithConcurrency } from '@/lib/concurrent'
import { validateFile } from '@/lib/attachmentMime'
import type { BufferedFile } from './EvidenceBuffer'

// Renderizador de Markdown do corpo do comentário. react-markdown é seguro por
// padrão (NÃO interpreta HTML cru → sem risco de XSS); remark-gfm habilita
// tabelas/listas de tarefa/riscado. Links externos abrem em nova aba.
const MD_COMPONENTS: Components = {
  a({ href, children, node: _node, ...rest }) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
        {children}
      </a>
    )
  },
}

function CommentBody({ body }: { body: string }) {
  return (
    <TypographyStylesProvider style={{ fontSize: '0.875rem' }}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
        {body}
      </ReactMarkdown>
    </TypographyStylesProvider>
  )
}

interface Attachment {
  id: number
  filename: string
  mime_type: string
  size_bytes: number
  uploader_name: string | null
  created_at: string
}

interface Comment {
  id: number
  author_user_id: number
  author_name: string
  is_internal: boolean
  body: string
  created_at: string
  attachments?: Attachment[]
}

interface CommentThreadProps {
  comments: Comment[]
  taskRequestId: number
  tenantId: number | string | undefined
  canViewInternal: boolean
  canComment: boolean
  /**
   * Espelha o guard do endpoint: `DELETE .../comments/{id}` exige Administrador
   * do ConsoleAI (`_require_caller_is_admin`, api.py). Sem isso o botao existiria
   * para quem sempre levaria 403 — affordance que nunca funciona e' pior do que
   * affordance nenhuma.
   */
  canDelete: boolean
}

function humanizeSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function CommentItem({
  comment,
  tenantId,
  taskRequestId,
  canDelete,
}: {
  comment: Comment
  tenantId: number | string | undefined
  taskRequestId: number
  canDelete: boolean
}) {
  const queryClient = useQueryClient()

  // Exclusao LOGICA no backend (deleted_at + deleted_by_user_id): a linha fica
  // na trilha, so sai da listagem. O texto da confirmacao diz isso, para
  // ninguem decidir com a ideia errada do que o botao faz.
  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(
        `/v1/tenants/${tenantId}/workflow/${taskRequestId}/comments/${comment.id}`,
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['chamado-comments', tenantId, taskRequestId],
      })
      notifications.show({
        color: 'teal',
        title: 'Comentário excluído',
        message: 'O comentário saiu da conversa; o registro fica na trilha de auditoria.',
      })
    },
    onError: (err) => {
      notifications.show({
        color: 'red',
        title: 'Erro ao excluir comentário',
        message: err instanceof Error ? err.message : String(err),
      })
    },
  })

  // Confirmacao pelo `modals.openConfirmModal` — mesmo caminho que o resto do
  // console usa para acao destrutiva (FinOps, WAF, KB, preferencias). O
  // `ModalsProvider` ja esta no shell (`main.tsx`).
  const askDelete = () =>
    modals.openConfirmModal({
      title: 'Excluir comentário',
      children: (
        <Stack gap="xs">
          <Text size="sm">
            O comentário sai da conversa do chamado. A exclusão é lógica: o registro
            continua na trilha de auditoria, com quem excluiu e quando.
          </Text>
          <Paper p="xs" withBorder bg="var(--mantine-color-gray-light)">
            <Text size="xs" c="dimmed" mb={4}>{comment.author_name}</Text>
            <Text size="sm" lineClamp={4}>{comment.body}</Text>
          </Paper>
        </Stack>
      ),
      labels: { confirm: 'Excluir', cancel: 'Cancelar' },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteMutation.mutate(),
    })

  const initials = (comment.author_name ?? 'U')
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase()

  const handleDownload = async (attachmentId: number, filename: string) => {
    try {
      const res = await api.get<{ url: string }>(
        `/v1/tenants/${tenantId}/workflow/${taskRequestId}/attachments/${attachmentId}`,
      )
      window.open(res.data.url, '_blank')
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Erro ao baixar',
        message: `${filename}: ${err instanceof Error ? err.message : String(err)}`,
      })
    }
  }

  return (
    <Paper
      id={`comment-${comment.id}`}
      p="sm"
      withBorder
      style={{
        borderLeft: comment.is_internal
          ? '3px solid var(--mantine-color-orange-4)'
          : '3px solid var(--mantine-color-blue-3)',
        backgroundColor: comment.is_internal
          ? 'var(--mantine-color-orange-light)'
          : undefined,
      }}
    >
      <Group mb="xs" gap="xs">
        <Avatar size="sm" color="blue" radius="xl">{initials}</Avatar>
        <Text size="sm" fw={500}>{comment.author_name}</Text>
        {comment.is_internal && (
          <Badge size="xs" color="orange" variant="light" leftSection={<IconLock size={8} />}>
            Interno
          </Badge>
        )}
        <Text size="xs" c="dimmed" ml="auto">
          {new Date(comment.created_at).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
        </Text>
        {canDelete && (
          <ActionIcon
            size="sm"
            variant="subtle"
            color="red"
            onClick={askDelete}
            aria-label={`Excluir comentário de ${comment.author_name}`}
            title="Excluir comentário"
          >
            <IconTrash size={14} />
          </ActionIcon>
        )}
      </Group>

      <CommentBody body={comment.body} />

      {comment.attachments && comment.attachments.length > 0 && (
        <Stack gap={4} mt="xs">
          {comment.attachments.map((a) => (
            <Group key={a.id} gap="xs" wrap="nowrap">
              <IconFile size={14} />
              <Text
                size="xs"
                component="button"
                type="button"
                onClick={() => handleDownload(a.id, a.filename)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  color: 'var(--mantine-color-blue-6)',
                  textDecoration: 'underline',
                  textAlign: 'left',
                }}
              >
                {a.filename}
              </Text>
              <Text size="xs" c="dimmed">({humanizeSize(a.size_bytes)})</Text>
              <ActionIcon
                size="xs"
                variant="subtle"
                color="blue"
                onClick={() => handleDownload(a.id, a.filename)}
                aria-label={`Baixar ${a.filename}`}
                title="Baixar"
              >
                <IconDownload size={12} />
              </ActionIcon>
            </Group>
          ))}
        </Stack>
      )}
    </Paper>
  )
}

export function CommentThread({
  comments, taskRequestId, tenantId, canViewInternal, canComment, canDelete,
}: CommentThreadProps) {
  const queryClient = useQueryClient()
  const [body, setBody] = useState('')
  const [isInternal, setIsInternal] = useState(false)
  const [files, setFiles] = useState<BufferedFile[]>([])
  const [editorTab, setEditorTab] = useState<string | null>('write')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const basePath = `/v1/tenants/${tenantId}/workflow/${taskRequestId}/attachments`

  // ── Toolbar de formatação Markdown ──────────────────────────────────────
  function applySelection(newValue: string, selStart: number, selEnd: number) {
    setBody(newValue)
    requestAnimationFrame(() => {
      const ta = textareaRef.current
      if (ta) { ta.focus(); ta.setSelectionRange(selStart, selEnd) }
    })
  }
  // Envolve a seleção (negrito/itálico/código/link).
  function wrapSelection(before: string, after: string, placeholder: string) {
    const ta = textareaRef.current
    const start = ta?.selectionStart ?? body.length
    const end = ta?.selectionEnd ?? body.length
    const sel = body.slice(start, end) || placeholder
    const next = body.slice(0, start) + before + sel + after + body.slice(end)
    applySelection(next, start + before.length, start + before.length + sel.length)
  }
  // Prefixa o início da linha atual (título/lista).
  function prefixLine(prefix: string) {
    const ta = textareaRef.current
    const start = ta?.selectionStart ?? body.length
    const lineStart = body.lastIndexOf('\n', start - 1) + 1
    const next = body.slice(0, lineStart) + prefix + body.slice(lineStart)
    applySelection(next, start + prefix.length, start + prefix.length)
  }
  const MD_ACTIONS = [
    { icon: IconBold, label: 'Negrito', run: () => wrapSelection('**', '**', 'texto') },
    { icon: IconItalic, label: 'Itálico', run: () => wrapSelection('_', '_', 'texto') },
    { icon: IconCode, label: 'Código', run: () => wrapSelection('`', '`', 'código') },
    { icon: IconHeading, label: 'Título', run: () => prefixLine('## ') },
    { icon: IconList, label: 'Lista', run: () => prefixLine('- ') },
    { icon: IconLink, label: 'Link', run: () => wrapSelection('[', '](https://)', 'texto') },
  ]

  function updateFileAt(index: number, patch: Partial<BufferedFile>) {
    setFiles((prev) => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)))
  }

  function handleAddFiles(selected: File[] | null) {
    if (!selected || selected.length === 0) return
    const accepted: BufferedFile[] = []
    for (const f of selected) {
      if (files.length + accepted.length >= 5) {
        notifications.show({
          color: 'red',
          title: 'Limite',
          message: 'Máximo 5 evidências por comentário',
        })
        break
      }
      const err = validateFile(f)
      if (err) {
        notifications.show({
          color: 'red',
          title: 'Arquivo rejeitado',
          message: `${f.name}: ${err}`,
        })
        continue
      }
      accepted.push({ file: f, status: 'pending', progress: 0 })
    }
    if (accepted.length > 0) setFiles((prev) => [...prev, ...accepted])
  }

  async function uploadCommentEvidence(
    commentId: number,
    bf: BufferedFile,
    index: number,
  ): Promise<void> {
    const mime = bf.file.type || 'application/octet-stream'

    updateFileAt(index, { status: 'uploading', progress: 5 })

    const presign = await api.post<{ presigned_put_url: string; s3_key: string }>(
      `${basePath}/presign`,
      { filename: bf.file.name, mime_type: mime, size_bytes: bf.file.size },
    )

    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('PUT', presign.data.presigned_put_url)
      xhr.setRequestHeader('Content-Type', mime)
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          updateFileAt(index, {
            progress: Math.min(95, Math.round((e.loaded / e.total) * 90) + 5),
          })
        }
      }
      xhr.onload = () =>
        xhr.status >= 200 && xhr.status < 300
          ? resolve()
          : reject(new Error(`PUT ${xhr.status}`))
      xhr.onerror = () => reject(new Error('network error'))
      xhr.send(bf.file)
    })

    await api.post(basePath, {
      s3_key: presign.data.s3_key,
      filename: bf.file.name,
      mime_type: mime,
      size_bytes: bf.file.size,
      comment_id: commentId,
    })

    updateFileAt(index, { status: 'done', progress: 100 })
  }

  const mutation = useMutation({
    mutationFn: async () => {
      // 1. cria comentário
      const res = await api.post<{ id: number }>(
        `/v1/tenants/${tenantId}/workflow/${taskRequestId}/comments`,
        { body, is_internal: isInternal, has_attachments: files.length > 0 },
      )
      const commentId = res.data.id

      // 2. upload paralelo se houver buffer
      if (files.length > 0) {
        const indexed = files.map((bf, i) => ({ bf, i }))
        const failed: Array<{ name: string; error: string }> = []
        await runWithConcurrency(
          indexed,
          3,
          async ({ bf, i }) => {
            try {
              await uploadCommentEvidence(commentId, bf, i)
            } catch (err) {
              const msg = err instanceof Error ? err.message : String(err)
              updateFileAt(i, { status: 'failed', error: msg })
              failed.push({ name: bf.file.name, error: msg })
            }
          },
          { failFast: false },
        )
        if (failed.length > 0) {
          notifications.show({
            color: 'yellow',
            title: 'Alguns anexos falharam',
            message: failed.map((f) => `${f.name}: ${f.error}`).join('; '),
          })
        }
      }

      return commentId
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chamado-comments', tenantId, taskRequestId] })
      queryClient.invalidateQueries({ queryKey: ['chamado', String(taskRequestId)] })
      queryClient.invalidateQueries({ queryKey: ['attachments', tenantId, taskRequestId] })
      setBody('')
      setFiles([])
      setIsInternal(false)
      notifications.show({ color: 'green', message: 'Comentário adicionado.' })
    },
    onError: (err: unknown) => {
      // TASK-10273: codigo de erro em snake_case na tela e' defeito por si so.
      const detalhe = formatApiError(err, 'Não foi possível enviar o comentário.')
      const message = detalhe === 'comentario_vazio'
        ? 'Escreva um comentário ou anexe um arquivo.'
        : detalhe
      notifications.show({ color: 'red', title: 'Erro ao enviar comentário', message })
    },
  })

  // Mais novo em cima: quem abre o chamado quer o ultimo passo, nao o primeiro.
  // Ordena aqui, sem depender da ordem que a API devolve.
  const filteredComments = (canViewInternal
    ? comments
    : comments.filter((c) => !c.is_internal)
  ).slice().sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))

  const canSubmit = (body.trim().length > 0 || files.length > 0) && !mutation.isPending

  return (
    <Stack gap="sm">
      <Text fw={500} size="sm">Comentários ({filteredComments.length})</Text>

      {canComment && (
        <>
          <Tabs value={editorTab} onChange={setEditorTab} variant="default">
            <Tabs.List>
              <Tabs.Tab value="write" leftSection={<IconPencil size={12} />}>Escrever</Tabs.Tab>
              <Tabs.Tab value="preview" leftSection={<IconEye size={12} />}>Pré-visualizar</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="write" pt="xs">
              <Group gap={2} mb={4}>
                {MD_ACTIONS.map(({ icon: Icon, label, run }) => (
                  <Tooltip key={label} label={label} withArrow>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      aria-label={label}
                      onClick={run}
                    >
                      <Icon size={15} />
                    </ActionIcon>
                  </Tooltip>
                ))}
                <Text size="xs" c="dimmed" ml="auto">Markdown suportado</Text>
              </Group>
              <Textarea
                ref={textareaRef}
                placeholder="Escreva seu comentário... (Markdown suportado)"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={3}
                minRows={2}
                autosize
              />
            </Tabs.Panel>
            <Tabs.Panel value="preview" pt="xs">
              <Paper withBorder p="sm" mih={80}>
                {body.trim()
                  ? <CommentBody body={body} />
                  : <Text size="sm" c="dimmed">Nada para pré-visualizar.</Text>}
              </Paper>
            </Tabs.Panel>
          </Tabs>

          {files.length > 0 && (
            <Stack gap="xs">
              {files.map((bf, i) => (
                <Group key={`${bf.file.name}-${i}`} gap="xs" wrap="nowrap">
                  <IconFile size={14} />
                  <Text size="xs" truncate style={{ flex: 1 }}>
                    {bf.file.name}
                  </Text>
                  <Text size="xs" c="dimmed">{humanizeSize(bf.file.size)}</Text>
                  {bf.status === 'uploading' && (
                    <>
                      <Progress value={bf.progress} size="xs" style={{ width: 60 }} />
                      <Text size="xs" c="blue">{bf.progress}%</Text>
                    </>
                  )}
                  {bf.status === 'done' && <IconCheck size={12} color="green" />}
                  {bf.status === 'failed' && (
                    <Text size="xs" c="red" title={bf.error}>!</Text>
                  )}
                  {bf.status !== 'uploading' && (
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      color="red"
                      aria-label={`Remover ${bf.file.name}`}
                      onClick={() =>
                        setFiles((prev) => prev.filter((_, idx) => idx !== i))
                      }
                    >
                      <IconX size={10} />
                    </ActionIcon>
                  )}
                </Group>
              ))}
            </Stack>
          )}

          <Group justify="space-between">
            {canViewInternal && (
              <Switch
                label="Comentário interno (apenas atendentes)"
                size="xs"
                checked={isInternal}
                onChange={(e) => setIsInternal(e.target.checked)}
              />
            )}
            <Group gap="xs" ml="auto">
              <FileButton
                onChange={handleAddFiles}
                multiple
                disabled={mutation.isPending || files.length >= 5}
              >
                {(props) => (
                  <ActionIcon
                    {...props}
                    variant="subtle"
                    aria-label="Anexar evidência"
                    title="Anexar evidência"
                  >
                    <IconPaperclip size={16} />
                  </ActionIcon>
                )}
              </FileButton>
              <Button
                size="xs"
                leftSection={<IconSend size={12} />}
                onClick={() => mutation.mutate()}
                loading={mutation.isPending}
                disabled={!canSubmit}
              >
                Enviar
              </Button>
            </Group>
          </Group>
          <Divider />
        </>
      )}

      {filteredComments.length === 0 && (
        <Text size="sm" c="dimmed" ta="center" py="md">
          Nenhum comentário ainda. Seja o primeiro a comentar.
        </Text>
      )}

      {filteredComments.map((c) => (
        <CommentItem
          key={c.id}
          comment={c}
          tenantId={tenantId}
          taskRequestId={taskRequestId}
          canDelete={canDelete}
        />
      ))}
    </Stack>
  )
}
